import { Button, Card, Icon, Link, Tag, Text } from "@ttc3k/trekker";
import { ArrowRight, MapPin, OpenNewWindow, Repeat } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { Box, BoxProps, Flex, styled } from "styled-system/jsx";
import {
  eventCarouselWidgetCardRecipe,
  EventCarouselWidgetCardRecipeProps,
} from "./recipe";
import { EnumEventType, EventManyQuery } from "gql/generated";
import {
  formatDistance,
  getFormattedDetailsLink,
  getMultilingualString,
} from "shared/utils";
import { SmartImage } from "shared/components/atoms";
import { MultilingualLangKey, DistanceUnit } from "shared/types";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";
import { useWidgetTrack } from "widgets/shared/WidgetTrackingContext";
import { useTrackImpression } from "shared/hooks/useTrackImpression";
import { useCalculateDistance } from "shared/hooks";
import _ from "lodash";
import { WhatsNearbyWidgetDetailsDisplayMode } from "widgets/whatsNearby/WhatsNearbyWidget";
import { useEventWidgetCard } from "shared/components/organisms/EventWidgetCard";
import { Locale } from "date-fns";

const StyledLink = styled(Link, {
  base: {
    display: "flex",
    gap: "50",
    color: "text.dark",
    fontWeight: "semiBold",
    transitionDuration: "normal",
    transitionProperty: "color",
    transitionTimingFunction: "default",
    _hover: {
      color: "text.accent.blue.mid",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

type EventCarouselWidgetCardProps = EventCarouselWidgetCardRecipeProps & {
  event: EventManyQuery["eventMany"][0];
  onClick?: (eventId: string) => void;
  viewOnly?: boolean;
  cardSurfaceProps?: BoxProps;
  lang?: MultilingualLangKey;
  detailsDisplayMode?: WhatsNearbyWidgetDetailsDisplayMode;
  detailsLink?: string;
  organizationId?: string;
  widgetId?: string;
  startingCoordinates?: [number, number]; // [latitude, longitude]
  distanceUnit?: DistanceUnit;
  locale?: Locale;
  ignoreImageTextAndNeverBlur?: boolean;
};

export const EventCarouselWidgetCard = ({
  event,
  onClick,
  orientation,
  viewOnly = false,
  cardSurfaceProps,
  lang = "en",
  detailsDisplayMode,
  detailsLink,
  organizationId,
  widgetId,
  startingCoordinates,
  distanceUnit,
  locale,
  ignoreImageTextAndNeverBlur: _ignoreImageTextAndNeverBlur = false,
}: EventCarouselWidgetCardProps) => {
  const classes = eventCarouselWidgetCardRecipe({ orientation });
  const { t } = useTranslation();
  const trackPostHogEvent = useWidgetTrack();
  const cardRef = useTrackImpression({
    organizationId,
    widgetId,
    eventId: event._id,
  });

  const eventTitle = getMultilingualString(event.title ?? {}, lang);

  // A real href is always rendered when a details-link template is configured
  // (even in slideout mode) so crawlers can discover the hosted pages.
  const detailsHref = detailsLink
    ? getFormattedDetailsLink(eventTitle, detailsLink, event._id)
    : undefined;
  const isLinkMode = !!detailsLink && detailsDisplayMode === "link";

  function trackDetailsClick() {
    if (viewOnly) return;
    trackPostHogEvent(
      detailsDisplayMode === "slideout"
        ? PostHogCustomEvent.DetailsClick
        : PostHogCustomEvent.ExpandedDetailsClick,
      {
        organizationId: organizationId,
        eventId: event._id,
        widgetId: widgetId,
      },
    );
  }

  function handleDetailsClick(e: React.MouseEvent) {
    // View-only previews render the href but must not navigate anywhere
    if (viewOnly) {
      e.preventDefault();
      return;
    }
    trackDetailsClick();
    // Link mode: let native anchor navigation open the hosted page
    // (also keeps cmd/middle-click behavior intact).
    if (isLinkMode) return;
    // Slideout mode: the href exists for crawlers and new-tab clicks only.
    e.preventDefault();
    onClick!(event._id);
  }

  // Space/Enter support. Enter already fires a native click on anchors with
  // an href, so only handle it when there is no href (else it double-fires).
  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (viewOnly) return;
    if (e.key === "Enter" && detailsHref) return;
    if (e.key !== " " && e.key !== "Enter") return;
    trackDetailsClick();
    if (isLinkMode) {
      window.open(detailsHref, "_blank", "noopener");
    } else {
      onClick!(event._id);
    }
  };

  const mainEventLocation = event.eventLocations?.[0];

  /* TODO: when Booker is added, handle click for Booker vs. Book now link */
  const handleBookNowClick = () => {
    if (!mainEventLocation?.contact?.links?.bookNow) return;
    trackPostHogEvent(PostHogCustomEvent.OpenBookNowLink, {
      bookNowLink: mainEventLocation.contact.links.bookNow,
    });
    window.open(mainEventLocation.contact.links.bookNow, "_blank", "noopener");
  };

  const previewImage = event.images?.[0];

  const isPortraitOrientation = orientation === "portrait";

  // The Card.Hero has a lot of unneeded css properties when the orientation is
  // horizontal they are not needed so use a Box in this case
  const HeroContainer = isPortraitOrientation ? Card.Hero : Box;

  const { distance } = useCalculateDistance(
    startingCoordinates,
    mainEventLocation?.contact?.address?.location?.coordinates &&
      _.compact(mainEventLocation.contact.address.location.coordinates)
        .length === 2
      ? [
          _.compact(mainEventLocation.contact.address.location.coordinates)[1],
          _.compact(mainEventLocation.contact.address.location.coordinates)[0],
        ]
      : undefined,
  );

  const { dateInfo } = useEventWidgetCard(event, lang, locale);

  return (
    <Card.Surface
      ref={viewOnly ? undefined : cardRef}
      part={"card__surface"}
      key={event._id}
      className={classes.surface}
      height={"full"}
      role="article"
      aria-label={getMultilingualString(event.title ?? {}, lang)}
      {...cardSurfaceProps}
    >
      <HeroContainer
        part={"card__hero"}
        className={classes.hero}
      >
        <SmartImage
          image={previewImage}
          width={isPortraitOrientation ? 400 : 240}
          height={isPortraitOrientation ? 200 : 262}
          shape="square"
          previewVideo={true}
          alt={eventTitle}
          parts={{
            container: "card__image-container",
            frame: "card__image-frame",
            image: "card__image",
          }}
        />
      </HeroContainer>
      <Card.Body className={classes.body} part={"card__body"}>
        <Box className={classes.textContent} part={"card__text-content"}>
          <Tag part={"card__tag"} visual="primary" mode="light">
            <styled.span
              display="flex"
              flexDir="row"
              alignItems="center"
              gap="100"
            >
              <span>
                {dateInfo.startDate}
                {dateInfo.otherDates.length > 0 && (
                  <span>
                    {dateInfo.otherDates.slice(0, 2).map(date => (
                      <span key={date}>
                        <styled.span color="text.light"> • </styled.span>
                        {date}
                      </span>
                    ))}
                    {dateInfo.otherDates.length > 2 && "..."}
                  </span>
                )}
              </span>
              {dateInfo.showRepeat ? (
                event.type === EnumEventType.Recurring ? (
                  <Icon Element={Repeat} />
                ) : (
                  <span style={{ marginLeft: "-4px" }}>...</span>
                )
              ) : null}
            </styled.span>
          </Tag>

          <Text
            visual={"bodySemiBold"}
            lineClamp={2}
            wordBreak="break-word"
            part={"card__title"}
            role="heading"
            aria-level={3}
          >
            {eventTitle}
          </Text>
          <Text
            part={"card__venue"}
            visual={"smallSemiBold"}
            lineClamp="1"
            wordBreak="break-all"
            color={"text.mid"}
            justifySelf="flex-end"
          >
            {getMultilingualString(event.eventOrganizer.venue)}
          </Text>
        </Box>
        <Card.BodyContent gap={"200"} minH={"38px"} alignItems={"center"} part="card__body-content">
          <StyledLink
            onClick={handleDetailsClick}
            onKeyUp={handleKeyUp}
            href={detailsHref}
            target={isLinkMode ? "_blank" : undefined}
            rel={isLinkMode ? "noopener" : undefined}
            part={"card__btn-details"}
            tabIndex="0"
            aria-label={t("widgets:OPERATORS_WIDGET.ARIA.VIEW_DETAILS", { name: eventTitle })}
          >
            {t("widgets:OPERATOR.CARD.SEE_DETAILS")}
            <Icon
              Element={
                detailsDisplayMode === "link" ? OpenNewWindow : ArrowRight
              }
            ></Icon>
          </StyledLink>
          {!!distance && distance > 0 && (
            <Flex gap={"50"} alignItems={"center"}>
              <Icon
                Element={MapPin}
                color={"icon.accent.blue.dark"}
                size={"sm"}
                part={"card__distance-icon"}
              />
              <Text
                visual={"smallSemiBold"}
                part={"card__distance-text"}
                color={"text.accent.blue.dark"}
              >
                {t("widgets:WHATS_NEARBY.DISTANCE_AWAY", {
                  distance: formatDistance(distance, distanceUnit),
                })}
              </Text>
            </Flex>
          )}
          {mainEventLocation?.contact?.links?.bookNow && (
            <Button
              visual={"accent.blue"}
              onClick={!viewOnly ? handleBookNowClick : undefined}
              part={"card__btn-book"}
            >
              {t("widgets:OPERATOR.CARD.BOOK_NOW")}
            </Button>
          )}
        </Card.BodyContent>
      </Card.Body>
    </Card.Surface>
  );
};
